import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster, toast } from 'sonner';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
import EnhancedResults from '@/pages/EnhancedResults';
import ResearchAssistant from '@/pages/ResearchAssistant';
import Quotes from '@/pages/Quotes';
import ResearchDesign from '@/pages/ResearchDesign';
import DataCleaning from '@/pages/DataCleaning';
import METAnalysis from '@/pages/METAnalysis';
import BiometricalAnalysis from '@/pages/BiometricalAnalysis';
import PromotionList from '@/pages/PromotionList';
import MolecularGenetics from '@/pages/MolecularGenetics';
import BKIntelligence from '@/pages/BKIntelligence';
import PhenoScan from '@/pages/PhenoScan';
import Reconstruction3D from '@/pages/Reconstruction3D';
import Predict from '@/pages/Predict';
import PredictMethodology from '@/pages/predict/Methodology';
import PredictValidation from '@/pages/predict/ValidationDashboard';
import PredictValidationLog from '@/pages/predict/ValidationLog';
import PredictAdminSubmitPilot from '@/pages/predict/AdminSubmitPilot';
import PredictHistoricalYield from '@/pages/predict/HistoricalYield';
import About from '@/pages/About';
import AdminFailures from '@/pages/AdminFailures';
import AdminWaitlist from '@/pages/AdminWaitlist';
import PolyploidQTL from '@/pages/PolyploidQTL';
import Pricing from '@/pages/Pricing';
import ProGate from '@/components/ProGate';
import ValidationDashboard from '@/pages/Validation';
import PipelineComparison from '@/components/PipelineComparison';
import ResponseSurface3D from '@/components/ResponseSurface3D';

const BACKEND_URL = (typeof window !== 'undefined' && window.location && window.location.origin) || process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// One-time "last free analysis" nudge — fires the moment a Free user drops to
// exactly 1 remaining credit (guarded by localStorage so it shows once per
// crossing). Safe to call anywhere; silently no-ops for Pro/unlimited users.
let _lastCreditChecking = false;
async function checkLastCreditNudge() {
  if (_lastCreditChecking) return;
  _lastCreditChecking = true;
  try {
    const { data } = await axios.get(`${API}/payments/usage`, { withCredentials: true });
    const KEY = 'bk_last_credit_toast';
    if (data && !data.unlimited && data.remaining === 1) {
      if (localStorage.getItem(KEY) !== '1') {
        localStorage.setItem(KEY, '1');
        toast.warning('Just 1 free analysis left', {
          description: 'Upgrade to Pro for unlimited analyses.',
          action: { label: 'Upgrade', onClick: () => { window.location.href = '/pricing'; } },
          duration: 9000,
        });
      }
    } else if (localStorage.getItem(KEY) === '1') {
      localStorage.removeItem(KEY);  // reset so a future crossing re-fires
    }
  } catch { /* best-effort nudge */ } finally {
    _lastCreditChecking = false;
  }
}

// Setup axios interceptor for automatic token refresh
axios.interceptors.response.use(
  (response) => {
    // After a successful analysis creation, check if the Free user just
    // crossed into their last credit and nudge them (fires once, any page).
    try {
      const cfg = response.config || {};
      const method = (cfg.method || '').toLowerCase();
      const url = cfg.url || '';
      if (method === 'post' && response.status < 300 &&
          (/\/analyze\//.test(url) || url.includes('/upload-and-analyze'))) {
        checkLastCreditNudge();
      }
    } catch { /* ignore */ }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't retry refresh calls or already-retried requests
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register')
    ) {
      originalRequest._retry = true;

      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return axios(originalRequest);
      } catch (refreshError) {
        // Refresh failed — redirect to login only if not already on a public auth route
        const path = window.location.pathname;
        const isPublicAuthRoute =
          path.includes('/login') ||
          path.includes('/register') ||
          path.includes('/forgot-password') ||
          path.includes('/reset-password') ||
          path === '/about' ||
          path.startsWith('/about/') ||
          path === '/predict/validation' ||
          path === '/predict/validation-log' ||
          path.startsWith('/predict/validation-log/');
        if (!isPublicAuthRoute) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Freemium cap — surface a clear upgrade nudge on any 402 (Free plan limit)
    if (error.response?.status === 402) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upgrade to Pro to continue.', {
        action: { label: 'Upgrade', onClick: () => { window.location.href = '/pricing'; } },
        duration: 8000,
      });
    }

    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — skip the /auth/me check so
    // AuthCallback can exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  // Human-friendly auth error messages keyed by HTTP status. Future
  // backend outages (502/503/504) now show a clear "server starting
  // up" hint with auto-retry hint instead of the generic "Something
  // went wrong" string that confused breeders during the Feb 2026
  // production-pod outage.
  const _AUTH_ERROR_BY_STATUS = {
    400: 'Email or password format is invalid. Please double-check both fields.',
    401: 'Email or password is incorrect. Please try again.',
    403: 'Your account is locked or disabled. Contact the administrator.',
    404: 'Login service unavailable on this domain. Refresh the page and try again.',
    408: 'Request timed out. Your internet may be slow — please try again.',
    409: 'An account with this email already exists. Try signing in instead.',
    422: 'Email or password missing or invalid format. Please check both fields.',
    429: 'Too many sign-in attempts. Please wait a minute and try again.',
    500: 'Server error. Please try again in a moment — we are auto-retrying.',
    502: 'Server is starting up or briefly unavailable. Auto-retrying in a moment…',
    503: 'Server is starting up or briefly unavailable. Auto-retrying in a moment…',
    504: 'Server took too long to respond. Auto-retrying in a moment…',
  };

  /**
   * Robust auth wrapper used by both `login` and `register`. Adds:
   *   - 15-second timeout (axios + AbortController).
   *   - One automatic retry on transient 5xx with a 1.5s backoff so
   *     a momentary backend pod restart looks transparent to the
   *     user (the most common failure mode in Feb 2026 outage).
   *   - Status-code-aware error messages so breeders see exactly
   *     what's wrong instead of "Something went wrong".
   *   - `console.error` includes the status + URL so future
   *     production debugging is one Network-tab glance away.
   */
  const _robustAuthPost = async (url, payload) => {
    const tryOnce = () => axios.post(url, payload, {
      withCredentials: true,
      timeout: 15000,
    });
    let last;
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        return await tryOnce();
      } catch (e) {
        last = e;
        const status = e.response?.status;
        const transient = status >= 500 && status <= 599;
        const isTimeout = e.code === 'ECONNABORTED';
        const isNetwork = e.code === 'ERR_NETWORK' || !e.response;
        const shouldRetry = attempt === 1 && (transient || isTimeout || isNetwork);
        if (!shouldRetry) break;
        // Backoff before retrying so a briefly-restarting pod has time
        // to come back up. 1500 ms covers the typical k8s pod
        // readiness-probe interval.
        await new Promise(r => setTimeout(r, 1500));
      }
    }
    throw last;
  };

  const _humaniseAuthError = (e) => {
    if (e.code === 'ECONNABORTED') {
      return 'Sign-in timed out after 15 seconds. Please check your internet connection and try again.';
    }
    if (e.code === 'ERR_NETWORK' || !e.response) {
      return 'Cannot reach the server. Please check your internet connection or try again in a moment.';
    }
    const status = e.response.status;
    if (_AUTH_ERROR_BY_STATUS[status]) {
      const detail = formatApiErrorDetail(e.response.data?.detail);
      // For 4xx prefer the server-supplied detail (it's specific).
      // For 5xx prefer our human-friendly message (server detail is
      // usually a stack trace fragment).
      if (status >= 500) return _AUTH_ERROR_BY_STATUS[status];
      return detail || _AUTH_ERROR_BY_STATUS[status];
    }
    return formatApiErrorDetail(e.response.data?.detail) || e.message || 'Sign-in failed. Please try again.';
  };

  const login = async (email, password) => {
    try {
      const { data } = await _robustAuthPost(`${API}/auth/login`, { email, password });
      setUser(data);
      return { success: true };
    } catch (e) {
      console.error('Login failed:', e.response?.status || e.code, e.config?.url);
      return { success: false, error: _humaniseAuthError(e) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await _robustAuthPost(`${API}/auth/register`, { email, password, name });
      setUser(data);
      return { success: true };
    } catch (e) {
      console.error('Register failed:', e.response?.status || e.code, e.config?.url);
      return { success: false, error: _humaniseAuthError(e) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (e) {
      console.error('Logout error:', e);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--page-bg)' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--green-500)', borderTopColor: 'transparent' }}></div>
          <p className="font-body" style={{ fontSize: 14, color: 'var(--text-medium)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Handles the return trip from Emergent-managed Google Auth. The browser
// lands at {origin}/dashboard#session_id=xxx — we exchange that one-time
// session_id server-side for our JWT cookies, then hand off to the app.
function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const processed = React.useRef(false);

  useEffect(() => {
    if (processed.current) return;   // guard against StrictMode double-run
    processed.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');
    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }
    (async () => {
      try {
        await axios.post(`${API}/auth/google/session`, {}, {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true,
        });
        await checkAuth();
        // strip the fragment and land on the dashboard
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate('/dashboard', { replace: true });
      } catch (e) {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || 'Google sign-in failed. Please try again.';
        toast.error(msg);
        navigate('/login', { replace: true });
      }
    })();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--page-bg)' }} data-testid="google-auth-callback">
      <div className="text-center">
        <div className="w-12 h-12 border-3 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--green-500)', borderTopColor: 'transparent' }}></div>
        <p className="font-body" style={{ fontSize: 14, color: 'var(--text-medium)' }}>Signing you in…</p>
      </div>
    </div>
  );
}

// Process an OAuth session_id BEFORE the normal router/ProtectedRoute runs.
function RootGate() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return <AppRoutes />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <RootGate />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  return (
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/forgot-password" element={<AuthPage />} />
          <Route path="/reset-password" element={<AuthPage />} />
          <Route path="/about" element={<About />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <Pricing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promotion-list"
            element={
              <ProtectedRoute>
                <PromotionList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/results/:id"
            element={
              <ProtectedRoute>
                <EnhancedResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={<Navigate to="/bk-intelligence" replace />}
          />
          <Route
            path="/assistant"
            element={<Navigate to="/bk-intelligence" replace />}
          />
          <Route
            path="/research-design"
            element={
              <ProtectedRoute>
                <ResearchDesign />
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-cleaning"
            element={
              <ProtectedRoute>
                <DataCleaning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/met-analysis"
            element={<Navigate to="/biometrical-analysis" replace />}
          />
          <Route
            path="/biometrical-analysis"
            element={
              <ProtectedRoute>
                <BiometricalAnalysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/molecular-genetics"
            element={
              <ProtectedRoute>
                <ProGate feature="Molecular & QTL analysis">
                  <MolecularGenetics />
                </ProGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/polyploid-qtl"
            element={
              <ProtectedRoute>
                <ProGate feature="Polyploid QTL mapping">
                  <PolyploidQTL />
                </ProGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/phenoscan"
            element={
              <ProtectedRoute>
                <PhenoScan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/3d-reconstruction"
            element={
              <ProtectedRoute>
                <Reconstruction3D />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bk-intelligence"
            element={
              <ProtectedRoute>
                <ProGate feature="BK Intelligence">
                  <BKIntelligence />
                </ProGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/predict"
            element={
              <ProtectedRoute>
                <Predict />
              </ProtectedRoute>
            }
          />
          <Route
            path="/predict/methodology/:module"
            element={
              <ProtectedRoute>
                <PredictMethodology />
              </ProtectedRoute>
            }
          />
          <Route
            path="/predict/validation"
            element={<PredictValidation />}
          />
          <Route
            path="/predict/validation-log"
            element={<PredictValidationLog />}
          />
          <Route
            path="/predict/admin/submit-pilot"
            element={
              <ProtectedRoute>
                <PredictAdminSubmitPilot />
              </ProtectedRoute>
            }
          />
          <Route
            path="/predict/historical-yield"
            element={
              <ProtectedRoute>
                <PredictHistoricalYield />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pipeline-comparison"
            element={
              <ProtectedRoute>
                <PipelineComparison />
              </ProtectedRoute>
            }
          />
          <Route
            path="/response-surface-3d"
            element={
              <ProtectedRoute>
                <ResponseSurface3D />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/failures"
            element={
              <ProtectedRoute>
                <AdminFailures />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/waitlist"
            element={
              <ProtectedRoute>
                <AdminWaitlist />
              </ProtectedRoute>
            }
          />
          <Route
            path="/validation"
            element={
              <ProtectedRoute>
                <ValidationDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
  );
}

export default App;
export { API, formatApiErrorDetail, checkLastCreditNudge };